import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MetricGroupCard from "./MetricGroupCard";
import { default as axios } from "../axiosConfig";
import Skeleton from "@mui/material/Skeleton";
import GroupIcon from "@mui/icons-material/Group";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { API_GetAdminUserSummary, API_GetExpiringJobContracts } from "../Assets/ApiUrlContainer";
import { ModalPrompt } from "./ModalPrompt";

function MetricCardSkeleton() {
  return (
    <div className="metric-card">
      <Skeleton variant="text" width={120} height={30} />
      <Skeleton variant="text" width={60} height={40} />
    </div>
  );
}

export default function AdminLandingPage({ user }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [expiringContractsModalToggleState, setExpiringContractsModalToggleState] = useState(false);
  const [statisticsModalToggleState, setStatisticsModalToggleState] = useState(false);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [contractStatistics, setContractStatistics] = useState([]);
  const [filteredContractStatistics, setFilteredContractStatistics] = useState([]);
  const [statsOverview, setStatsOverview] = useState("Loading...");
  const navigate = useNavigate();
  const clipboardButtonRef = useRef(null);

  const isLoading = !summary && !error;

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await axios.get(API_GetAdminUserSummary);
        setSummary(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching admin landing summary:", err);
        setError("Failed to load dashboard data. Please try refreshing the page.");
      }
    };

    fetchSummary();
  }, []);

  useEffect(() => {
    if (summary === null) {
      return;
    }

    let totalEarnedCad = 0;
    let totalExpectedCad = 0;
    let totalEarnedUsd = 0;
    let totalExpectedUsd = 0;

    for (let stat of summary.contracts.statistics) {
      if (stat.Currency === "CAD") {
        totalEarnedCad += stat.ActualProfit;
        totalExpectedCad += stat.ExpectedProfit;
      }

      if (stat.Currency === "USD") {
        totalEarnedUsd += stat.ActualProfit;
        totalExpectedUsd += stat.ExpectedProfit;
      }
    }

    let overViewDetails = `
    <span style="font-size: 1.125rem">
      Total Profit: $${totalEarnedCad.toFixed(2)} CAD / $${totalEarnedUsd.toFixed(2)} USD
      <br>
      Expected Profit: $${totalExpectedCad.toFixed(2)} CAD / $${totalExpectedUsd.toFixed(2)} USD
      </span>
    `;

    setStatsOverview(overViewDetails);

    let stats = summary.contracts.statistics.map(stat => ({
      ...stat,
      Month: `${stat.Month} ${stat.Year}`,
      HoursExpected: stat.HoursExpected.toFixed(2),
      ContractorRate: `$${stat.ContractorRate}`,
      TgsRate: `$${stat.TgsRate}`,
      ExpectedProfit: `$${stat.ExpectedProfit.toFixed(2)}`,
      ActualProfit: `$${stat.ActualProfit.toFixed(2)}`
    }));

    setContractStatistics(stats);
    setFilteredContractStatistics(stats);
  }, [summary])

  useEffect(() => {
    const fetchExpiringContracts = async () => {
      try {
        const response = await axios.get(`${API_GetExpiringJobContracts}/${user.Id}/${user.IsGoogleUser}`);
        setExpiringContracts(response.data || []);

      } catch (err) {
        console.log("Error fetching the expiring contracts: ", err);
      }
    }

    fetchExpiringContracts();
  }, [user])

  const openExpiringContractsModal = () => setExpiringContractsModalToggleState(!expiringContractsModalToggleState);
  const openStatisticsModal = () => setStatisticsModalToggleState(!statisticsModalToggleState);

  const copyTextToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      clipboardButtonRef.current.innerText = "Copied!";
      setTimeout(() => clipboardButtonRef.current.innerText = "Export to Clipboard", 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      clipboardButtonRef.current.innerText = "Copy Failed";
      setTimeout(() => clipboardButtonRef.current.innerText = "Export to Clipboard", 2000);
    }
  }

  const handleExportToClipboard = () => {
    let clipboardText = "Company\tCandidate\tMonth\tHrs Worked\tHrs Expected\tContractor Rate\tTGS Rate\tExpected Profit\tActual Profit\tCurrency\n";
    for (let stat of filteredContractStatistics) {
      clipboardText += `${stat.CompanyName}\t${stat.CandidateName}\t${stat.Month}\t${stat.HoursWorked}\t${stat.HoursExpected}\t${stat.ContractorRate}\t${stat.TgsRate}\t${stat.ExpectedProfit}\t${stat.ActualProfit}\t${stat.Currency}\n`;
    }

    copyTextToClipboard(clipboardText);
  }

  const handleStatFiltering = (event) => {
    let filterString = event.target.value;
    if (filterString == "") setFilteredContractStatistics(contractStatistics);
    else {
      let filteredResults = contractStatistics.filter(stat => (
        stat.CompanyName.toLowerCase().includes(filterString.toLowerCase()) ||
        stat.CandidateName.toLowerCase().includes(filterString.toLowerCase()) ||
        stat.Month.toLowerCase().includes(filterString.toLowerCase()) ||
        stat.Currency.toLowerCase().includes(filterString.toLowerCase())
      ));

      setFilteredContractStatistics(filteredResults);
    }
  }

  const handleStatsModalClosed = () => {
    setStatisticsModalToggleState(!statisticsModalToggleState);
    setFilteredContractStatistics(contractStatistics);
  }

  return (
    <>
      <div className="landing-content">
        <div className="metric-panel">
          <div className="welcome-header">
            <h1>Welcome {user.FirstName} {user.LastName}</h1>
          </div>

          {error && <div className="error-message">{error}</div>}

          {isLoading ? (
            <div className="metric-group">
              <div className="landing-grid">
                {[...Array(9)].map((_, i) => (
                  <MetricCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Two-column grid for summary sections */}
              <div className="grid-two-columns">
                {/* Left Column */}
                <div className="multGroupedCards">
                  <MetricGroupCard
                    className="group-card group-user"
                    title={<><GroupIcon style={{ marginRight: "8px" }} />User Summary</>}
                    metrics={[
                      {
                        label: "Active Users",
                        value: summary.totals.activeUsers ?? 0
                      },
                      {
                        label: "Active Consultants",
                        value: summary.totals.activeConsultants ?? 0
                      },
                      {
                        label: "New Users This Week",
                        value: summary.totals.newUsers ?? 0
                      }
                    ]}
                  />

                  <MetricGroupCard
                    className="group-card group-contracts"
                    title={<><CalendarMonthIcon style={{ marginRight: "8px" }} />Contracts</>}
                    metrics={[
                      {
                        label: "Contracts Expiring Soon",
                        value: summary.contracts.expiringSoon ?? 0,
                        onClick: () => openExpiringContractsModal()
                      }
                    ]}
                  />
                </div>

                {/* Right Column */}
                <div className="multGroupedCards">
                  <MetricGroupCard
                    className="group-card group-jobs"
                    title={<><WorkOutlineIcon style={{ marginRight: "8px" }} />Job Activity</>}
                    metrics={[
                      {
                        label: "New Jobs This Week",
                        value: summary.jobs.newJobsThisWeek ?? 0,
                        onClick: () => navigate("/jobs")
                      },
                      {
                        label: "Applications This Week",
                        value: summary.jobs.applicationsThisWeek ?? 0
                      }
                    ]}
                  />

                  <MetricGroupCard
                    className="group-card group-contracts"
                    title={<><TrendingUpIcon style={{ marginRight: "8px" }} />Contract Statistics</>}
                    metrics={[
                      {
                        label: "Contract Statistics",
                        value: statsOverview,
                        onClick: () => openStatisticsModal()
                      }
                    ]}
                  />
                </div>
              </div>

              {/* Pending Approvals Full Width */}
              <MetricGroupCard
                className="group-card group-approvals"
                title={<><AssignmentTurnedInIcon style={{ marginRight: "8px" }} />Pending Approvals</>}
                metrics={[
                  {
                    label: "Leave Requests",
                    value: summary.approvals.leaveRequests ?? 0,
                    onClick: () => navigate("/tasks/leaverequests")
                  },
                  {
                    label: "Forms to Review",
                    value: summary.approvals.formsNeedingApproval ?? 0,
                    onClick: () => navigate("/forms")
                  },
                  {
                    label: "Timesheets to Approve",
                    value: summary.approvals.timesheetsNeedingApproval ?? 0,
                    onClick: () => navigate("/tasks?tab=timesheets")
                  }
                ]}
              />
            </>
          )}
        </div>
      </div>

      <ModalPrompt
        onModalClose={() => setExpiringContractsModalToggleState(!expiringContractsModalToggleState)}
        customModalContainerStyles={expiringContracts.length > 0 && { width: '50% !important' }}
        customModalCloseStyles={{ color: '#172564' }}
        modalToggleState={expiringContractsModalToggleState}
        modalTitle={"Expiring Job Contacts"}
        modalContent={
          <>
            <div className="expiringContractsModal">
              {expiringContracts.length === 0 && (
                <div className="noContracts">
                  <span>There are no contracts expiring within 30 days</span>
                </div>
              )}

              {expiringContracts.length > 0 && (
                <>
                  <div className="contractEntryHeading">
                    <div className="jobName">Job</div>
                    <div className="candidateName">Candidate</div>
                    <div className="expiration">Expiration Date</div>
                  </div>

                  <div className="contractEntryDataContainer">
                    {expiringContracts.map(contract => (
                      <div className="contractEntryData" key={contract.ContractId}>
                        <div className="jobName"><div className="spanText">{contract.JobName}</div></div>
                        <div className="candidateName"><div className="spanText">{contract.CandidateName}</div></div>
                        <div className="expiration"><div className="spanText">{contract.ContractExpireDate.split("T")[0]}</div></div>
                      </div>
                    ))}
                  </div>
                </>
              )}

            </div>
          </>
        }
      />

      <ModalPrompt
        onModalClose={() => handleStatsModalClosed()}
        customModalContainerStyles={contractStatistics.length > 0 && { width: '95% !important' }}
        customModalCloseStyles={{ color: '#172564' }}
        modalToggleState={statisticsModalToggleState}
        modalTitle={"TGS Contract Profit Margin Statistics"}
        modalContent={
          <>
            <div className="statisticsModal">
              {contractStatistics.length === 0 && (
                <div className="noStats">
                  <span>There are no contract statistics generated yet</span>
                </div>
              )}

              {contractStatistics.length > 0 && (
                <>
                  <div className="exportToCSVButton">
                    <button onClick={handleExportToClipboard} ref={clipboardButtonRef}>Export to Clipboard</button>
                  </div>

                  <div className="filterBar">
                    <input id="statFilter" placeholder="Search by company, candidate, month, or currency" onInput={handleStatFiltering} />
                  </div>

                  <div className="statisticsEntryHeading">
                    <div className="jobName">Company</div>
                    <div className="candidateName">Candidate</div>
                    <div className="month">Month</div>
                    <div className="hrsWorks">Hrs Worked</div>
                    <div className="hrsExpected">Hrs Expected</div>
                    <div className="contractorRate">Contractor Rate</div>
                    <div className="tgsRate">TGS Rate</div>
                    <div className="expectedProfit">Expected Profit</div>
                    <div className="actualProfit">Actual Profit</div>
                    <div className="currency">Currency</div>
                  </div>

                  <div className="statisticsEntryDataContainer">
                    {filteredContractStatistics.map(stat => (
                      <div className="statisticsEntryData" key={stat.Id}>
                        <div className="jobName"><div className="spanText">{stat.CompanyName}</div></div>
                        <div className="candidateName"><div className="spanText">{stat.CandidateName}</div></div>
                        <div className="month"><div className="spanText">{stat.Month}</div></div>
                        <div className="hrsWorks"><div className="spanText">{stat.HoursWorked}</div></div>
                        <div className="hrsExpected"><div className="spanText">{stat.HoursExpected}</div></div>
                        <div className="contractorRate"><div className="spanText">{stat.ContractorRate}</div></div>
                        <div className="tgsRate"><div className="spanText">{stat.TgsRate}</div></div>
                        <div className="expectedProfit"><div className="spanText">{stat.ExpectedProfit}</div></div>
                        <div className="actualProfit"><div className="spanText">{stat.ActualProfit}</div></div>
                        <div className="currency"><div className="spanText">{stat.Currency}</div></div>
                      </div>
                    ))}
                  </div>
                </>
              )}

            </div>
          </>
        }
      />
    </>
  );
}